import { useGetOneNetwork } from "#hooks/adapters/useNetworks";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { useNavigate, useParams } from "#routers/hooks";
import { linkToEquipmentDetail, linkToFacilities } from "#routers/links";
import { NetworkDetailPageParameters } from "#routes/organization/networks/[networkId]/detail";
import { NETWORK_ENTITY_INITIAL_SORT } from "#routes/organization/networks/[networkId]/detail/NetworkDetailsTab/NetworkEntityTablePanel/NetworkEntityTablePanel";
import { EquipmentTableFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  DataTablePanel,
  HeaderType,
  type StorageKeys,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  EquipmentAdapter,
  EquipmentType,
} from "@validereinc/domain";
import React from "react";
import { NetworkEditRoute } from "../../edit";

export const NetworkEquipmentPanel = ({
  filterConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
}: Pick<
  StorageKeys,
  "filterConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
>) => {
  const { localize } = useLocalization();
  const { networkId } = useParams<NetworkDetailPageParameters>();
  const navigate = useNavigate();
  const { data: assetGroup } = useGetOneNetwork(networkId);
  const getAssetIdsByType = (assetType: AssetTypeType) =>
    // @ts-expect-error TODO: Typing on assetGroup
    assetGroup?.assets
      ?.filter(({ asset_type }) => asset_type === assetType)
      .map(({ id }) => id) ?? [];
  const equipmentIds = getAssetIdsByType?.(AssetType.EQUIPMENT);
  const [tableFilters] = useStickyState({}, filterConfigStorageKey);
  const filters = { ...tableFilters, id: equipmentIds };
  const [tableState, updateTableState] = useTableSortingAndPagination(
    NETWORK_ENTITY_INITIAL_SORT,
    filters,
    tableStateStorageKey
  );

  const equipmentParams: Parameters<typeof EquipmentAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters,
  };

  const isEquipmentQueryEnabled = !!equipmentIds?.length;

  const { data, isLoading } = useQuery({
    queryKey: ["equipment", equipmentParams],
    queryFn: () => EquipmentAdapter.getList(equipmentParams),
    enabled: isEquipmentQueryEnabled,
  });

  const headers: Array<HeaderType<EquipmentType>> = [
    {
      key: "name",
      label: "Name",
      isSortable: true,
      renderComponent: ({ item }) => (
        <RoutingLink to={linkToEquipmentDetail(item.id)}>
          {item.name}
        </RoutingLink>
      ),
    },
    {
      key: "facility_id",
      label: "Facility",
      isSortable: true,
      renderComponent: ({ item }) => (
        <RoutingLink to={linkToFacilities(item.facility_id)}>
          {item.facility.name}
        </RoutingLink>
      ),
    },
    {
      key: "type.name",
      label: "Type",
      isSortable: true,
    },
  ];

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{
        title: `Network ${localize("equipment_plural")}`,
        actionRow: (
          <>
            <Button
              onClick={() => {
                navigate({
                  pathname: NetworkEditRoute.toLink({
                    pathParams: { networkId },
                  }),
                });
              }}
            >
              Edit
            </Button>
          </>
        ),
      }}
      dataTableProps={{
        intent: "simplicity-first",
        isLoading: isEquipmentQueryEnabled && isLoading,
        headers,
        items: data?.data ?? [],
        getItemId: (item) => item.id,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: data?.total_entries,
        },
      }}
      filterComponent={
        <EquipmentTableFilterArea
          filterConfigStorageKey={filterConfigStorageKey}
        />
      }
    />
  );
};
